import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Accordion,
  Form,
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import LeftNav from "../../common/leftnav";
import ChooseVoice from "./choose_voice/choose_voice";

const ModelConfiguration = () => {
  const [loading, setLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState(0); // Track the selected accordion
  const [temperature, setTemperature] = useState(0.5); // Add temperature state
  const navigate = useNavigate();

  const models = [
    {
      id: 0,
      title: "GPT – 4o Realtime",
      description:
        "The advanced model with vision, voice, and real-time speed.",
      capabilities: "Complex reasoning, content creation, and multimodal task",
      latency: "500–800ms",
      intelligence: "90%",
      speed: "65%",
      cost: "95%",
      status: "Selected",
    },
    {
      id: 1,
      title: "GPT – 3.5 Turbo",
      description: "A faster and cost-effective model for general tasks.",
      capabilities:
        "General reasoning, conversational AI, and lightweight tasks.",
      latency: "300–500ms",
      intelligence: "80%",
      speed: "75%",
      cost: "85%",
      status: "Select Model",
    },
  ];

  const additionalModels = [
    {
      id: 2,
      title: "GPT – 3 Standard",
      description: "A reliable model for everyday tasks and queries.",
      capabilities: "Basic reasoning, conversational AI, and simple tasks.",
      latency: "200–400ms",
      intelligence: "70%",
      speed: "85%",
      cost: "75%",
      status: "Select Model",
    },
    {
      id: 3,
      title: "GPT – 2 Legacy",
      description: "An older model for lightweight and cost-effective tasks.",
      capabilities: "Simple reasoning and lightweight conversational AI.",
      latency: "150–300ms",
      intelligence: "60%",
      speed: "90%",
      cost: "50%",
      status: "Select Model",
    },
  ];

  const handleModelSelection = (modelId) => {
    setSelectedModel(modelId);

    // Set the temperature based on the selected model
    const modelTemperatureMap = {
      0: 0.5, // 50% for GPT – 4o Realtime
      1: 0.6, // 60% for GPT – 3.5 Turbo
      2: 0.7, // 70% for GPT – 3 Standard
      3: 0.9, // 80% for GPT – 2 Legacy
    };

    const newTemperature = modelTemperatureMap[modelId] || 0.5; // Default to 50% if not mapped
    setTemperature(newTemperature);

    // Update the slider's background
    const slider = document.querySelector(".temperature-range");
    if (slider) {
      slider.style.setProperty("--progress", `${newTemperature * 100}%`);
    }
  };

  const handleTemperatureChange = (e) => {
    const value = parseFloat(e.target.value);
    setTemperature(value);

    // Dynamically update the slider's background
    const slider = e.target;
    slider.style.setProperty("--progress", `${value * 100}%`); // Update the CSS variable for the progress
  };

  return (
    <div className="d-flex">
      <LeftNav />
      <Container
        fluid
        className="model-configuration-content flex-grow-1 p-4 bg-light"
      >
        <Row>
          <Col>
            <h2 className="fs-five-title fw-bold">Agent Settings</h2>
            <p className="gray-color">
              Customize Nouraa to enhance your business call experience.
            </p>
          </Col>
        </Row>
        <Row>
          <Col>
            <Card className="p-4 mt-4 border-0">
              <div>
                <Link
                  to="/agent_settings"
                  className="text-decoration-none purple-color fw-bold mb-4"
                  onClick={() => {
                    setLoading(true);
                    navigate("/agent_settings");
                  }}
                >
                  <img
                    src="../assets/arrow-left.svg"
                    alt="Arrow Right"
                    className="me-1"
                  />
                  Go Back
                </Link>
                <div className="d-flex justify-content-between align-items-center mt-4">
                  <h2 className="fs-two fw-bold">
                    <img
                      src="../assets/oui_app-advanced-settings.svg"
                      alt="Arrow Right"
                      className="me-2"
                    />
                    Model Configuration
                  </h2>
                </div>
                <p className="gray-color">
                  Fine-tune Nouraa’s performance using advanced options
                </p>
                <hr className="mb-4 border-dark-subtle" />
                {loading ? (
                  <div className="text-center">
                    <Spinner animation="border" />
                  </div>
                ) : (
                  <Row>
                    <Col>
                      <Card className="p-4 mt-4 rounded-4 border">
                        <h5 className="fw-bold">Choose Model</h5>
                        <hr className="mb-4 border-dark-subtle" />
                        <Row className="g-3">
                          {models.map((model) => (
                            <Col md={6} key={model.id} className="d-flex">
                              <Accordion
                                defaultActiveKey={model.id.toString()}
                                className={`w-100 ${
                                  selectedModel === model.id
                                    ? "selected-card"
                                    : ""
                                }`}
                                style={{
                                  height: "100%",
                                  border:
                                    selectedModel === model.id
                                      ? "2px solid #6f42c1" // Highlight border for selected accordion
                                      : " ",
                                }}
                              >
                                <Accordion.Item
                                  eventKey={model.id.toString()}
                                  onClick={() => handleModelSelection(model.id)} // Update selected accordion and temperature
                                >
                                  <Accordion.Header>
                                    <div className="d-flex align-items-center">
                                      <img
                                        src="../assets/arcticons_openai-chatgpt.svg"
                                        alt="Icon"
                                        className="me-2"
                                      />
                                      <div>
                                        <h5 className="fw-bold">
                                          {model.title}
                                        </h5>
                                        <p className="text-muted mb-1 small-text">
                                          {model.description}
                                        </p>
                                      </div>
                                    </div>
                                  </Accordion.Header>
                                  <Accordion.Body>
                                    {/* Inside Body */}
                                    <div className="mt-2">
                                      <strong>Capabilities</strong>
                                      <p className="mb-2">
                                        {model.capabilities}
                                      </p>

                                      <div className="d-flex">
                                        <strong>
                                          Latency{" "}
                                          <img
                                            src="../assets/info-sm.svg"
                                            alt="info"
                                            className="me-2"
                                          />
                                        </strong>
                                        <p className="text-muted mb-3 ms-5">
                                          {model.latency}
                                        </p>
                                      </div>
                                    </div>

                                    {/* Progress Bars */}
                                    <div>
                                      {[
                                        {
                                          label: "Intelligence",
                                          icon: "Vector.svg",
                                          color: "bg-purple",
                                          value: model.intelligence,
                                        },
                                        {
                                          label: "Speed",
                                          icon: "Vector-1.svg",
                                          color: "bg-info",
                                          value: model.speed,
                                        },
                                        {
                                          label: "Cost",
                                          icon: "Vector-2.svg",
                                          color: "bg-success",
                                          value: model.cost,
                                        },
                                      ].map((item, index) => (
                                        <div
                                          className="d-flex align-items-center mt-2 progress-bar-container"
                                          key={index}
                                        >
                                          <span className="progress-label">
                                            <img
                                              src={`../assets/${item.icon}`}
                                              alt={item.label}
                                              className="me-2"
                                            />
                                            {item.label}
                                          </span>
                                          <div className="progress  ms-3 progress-bar-aligned">
                                            <div
                                              className={`progress-bar ${item.color}`}
                                              style={{ width: item.value }}
                                            ></div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>

                                    {/* Status */}
                                    <div className="text-center mt-3">
                                      <div
                                        className={`fw-bold ${
                                          selectedModel === model.id
                                            ? "purple-color"
                                            : "text-muted"
                                        }`}
                                      >
                                        {selectedModel === model.id
                                          ? "Selected"
                                          : "Select Model"}
                                      </div>
                                    </div>
                                  </Accordion.Body>
                                </Accordion.Item>
                              </Accordion>
                            </Col>
                          ))}
                        </Row>

                        <Row className="g-3 mt-2">
                          {additionalModels.map((model) => (
                            <Col md={6} key={model.id} className="d-flex">
                              <Accordion
                                className={`w-100 ${
                                  selectedModel === model.id
                                    ? "selected-card"
                                    : ""
                                }`}
                                style={{
                                  height: "100%",
                                  border:
                                    selectedModel === model.id
                                      ? "2px solid #6f42c1"
                                      : "",
                                }}
                              >
                                <Accordion.Item
                                  eventKey={model.id.toString()}
                                  onClick={() => handleModelSelection(model.id)}
                                >
                                  <Accordion.Header>
                                    <div className="d-flex align-items-center">
                                      <img
                                        src="../assets/arcticons_openai-chatgpt.svg"
                                        alt="Icon"
                                        className="me-2"
                                      />
                                      <div>
                                        <h5 className="fw-bold">
                                          {model.title}
                                        </h5>
                                        <p className="text-muted mb-1 small-text">
                                          {model.description}
                                        </p>
                                      </div>
                                    </div>
                                  </Accordion.Header>
                                  <Accordion.Body>
                                    <div className="mt-2">
                                      <strong>Capabilities</strong>
                                      <p className="mb-2">
                                        {model.capabilities}
                                      </p>

                                      <div className="d-flex">
                                        <strong>
                                          Latency{" "}
                                          <img
                                            src="../assets/info-sm.svg"
                                            alt="info"
                                            className="me-2"
                                          />
                                        </strong>
                                        <p className="text-muted mb-3 ms-5">
                                          {model.latency}
                                        </p>
                                      </div>
                                    </div>

                                    <div>
                                      {[
                                        {
                                          label: "Intelligence",
                                          icon: "Vector.svg",
                                          color: "bg-purple",
                                          value: model.intelligence,
                                        },
                                        {
                                          label: "Speed",
                                          icon: "Vector-1.svg",
                                          color: "bg-info",
                                          value: model.speed,
                                        },
                                        {
                                          label: "Cost",
                                          icon: "Vector-2.svg",
                                          color: "bg-success",
                                          value: model.cost,
                                        },
                                      ].map((item, index) => (
                                        <div
                                          className="d-flex align-items-center mt-2 progress-bar-container"
                                          key={index}
                                        >
                                          <span className="progress-label">
                                            <img
                                              src={`../assets/${item.icon}`}
                                              alt={item.label}
                                              className="me-2"
                                            />
                                            {item.label}
                                          </span>
                                          <div className="progress  ms-3 progress-bar-aligned">
                                            <div
                                              className={`progress-bar ${item.color}`}
                                              style={{ width: item.value }}
                                            ></div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>

                                    <div className="text-center mt-3">
                                      <div
                                        className={`fw-bold ${
                                          selectedModel === model.id
                                            ? "purple-color"
                                            : "text-muted"
                                        }`}
                                      >
                                        {selectedModel === model.id
                                          ? "Selected"
                                          : "Select Model"}
                                      </div>
                                    </div>
                                  </Accordion.Body>
                                </Accordion.Item>
                              </Accordion>
                            </Col>
                          ))}
                        </Row>
                        <div className="px-2 pt-5 dashed-border-top mt-5">
                          <Form.Group>
                            <Row className="align-items-center">
                              <Col md={8}>
                                <div className="fw-semibold">
                                  LLM Temperature Control
                                </div>
                                <div className="helper-text">
                                  Lower value yields better function call
                                  results.
                                </div>
                              </Col>
                              <Col
                                md={4}
                                className="d-flex flex-column align-items-end"
                              >
                                <Form.Range
                                  min={0}
                                  max={1}
                                  step={0.01}
                                  value={temperature}
                                  onChange={handleTemperatureChange}
                                  className="temperature-range"
                                />
                                <div className="d-flex justify-content-between w-100 mt-1">
                                  <span className="text-muted">Low</span>
                                  <span className="text-muted">High</span>
                                </div>
                              </Col>
                            </Row>
                          </Form.Group>
                        </div>
                      </Card>
                    </Col>
                  </Row>
                )}
              </div>
              <ChooseVoice />
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ModelConfiguration;
